import { DateField } from "@/features/FilterTable/DateField";
import { dateSchema, formatDateValueToISOString, parseToDateValue } from "@/features/FormCard/utils";
import { useCurrentPageFromUrl } from "@/hooks/useClientActions";
import { postClientAction } from "@/lib/fetch";
import { Clients } from "@/lib/clients";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

const schema = z.object({
  reportingDate: z.preprocess((value) => parseToDateValue(value as string), dateSchema).describe("date"),
});
type FormValues = z.infer<typeof schema>;

export default function I1GenerateExport() {
  const { t } = useTranslation();
  const currentPage = useCurrentPageFromUrl();
  const [isOpen, setIsOpen] = useState(false);
  const { handleSubmit, setValue, watch, register, reset } = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
  });
  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const reportingDate = formatDateValueToISOString(values.reportingDate);
      if (!reportingDate) throw new Error(t("reports.reporting_date_required"));
      return postClientAction(`${import.meta.env.VITE_BACKEND_URL}/i1/i1_download_center/run-export`, {
        client: Clients.I1.toUpperCase(), prefix: Clients.I1, reportingDate,
        action_button_name: t("reports.generate_i1_export"), action_execution_page: currentPage ?? "",
      });
    },
    onSuccess: () => { toast.success(t("reports.generate_i1_export_started")); setIsOpen(false); reset(); },
    onError: () => toast.error(t("reports.generate_error")),
  });
  return (
    <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement="bottom" showArrow>
      <PopoverTrigger>
        <Button
          size="sm"
          radius="full"
          style={{
            border: "solid 2px transparent",
            backgroundImage: `linear-gradient(hsl(var(--heroui-background)), hsl(var(--heroui-background))), linear-gradient(83.87deg, #F54180, #9353D3)`,
            backgroundOrigin: "border-box",
            backgroundClip: "padding-box, border-box",
          }}
          endContent={<ChevronDownIcon className="h-4 w-4" />}
        >
          {t("reports.generate_i1_export")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="space-y-4 p-1 w-full">
          <DateField name="reportingDate" label={t("reports.reporting_date")} watch={watch} setValue={setValue} register={register} required />
          <Button type="submit" color="primary" size="sm" isLoading={mutation.isPending}>{t("actions.generate")}</Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
